import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { categoriesAdminApi, type CategoryAdminRow } from "@/lib/products-admin";
import { useProductCategoryTree, productKeys, type ProductCategoryNode } from "@/lib/products";
import { ListPage } from "@/archetypes/ListPage";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { RowActions } from "@/components/row-actions";
import { useConfirm } from "@/hooks/use-confirm";

// ---------------------------------------------------------------------------
// Zod schema
// ---------------------------------------------------------------------------

const categorySchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  parentCategoryId: z.number().int().nullable().optional(),
});

type CategoryForm = z.infer<typeof categorySchema>;

const BLANK_FORM: CategoryForm = {
  name: "",
  parentCategoryId: null,
};

// ---------------------------------------------------------------------------
// Tree row types (flat row with an extra "indent" indicator for rendering)
// ---------------------------------------------------------------------------

interface TreeRow {
  node: ProductCategoryNode;
  /** Cached admin RowVersion/id from the write-endpoint response (if available) */
  adminRow?: CategoryAdminRow;
  isChild: boolean;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function CategoriesAdminPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ProductCategoryNode | null>(null);

  // ---- Read side: pull from the shared product category tree ----
  const { data: categories } = useProductCategoryTree();

  // ---- Build sorted flat list: top-level first, children indented below parent ----
  const rows = useMemo<TreeRow[]>(() => {
    const nodes = categories ?? [];
    const topLevel = nodes.filter((n) => n.parentCategoryId == null);
    const childrenByParent = new Map<number, ProductCategoryNode[]>();
    for (const n of nodes) {
      if (n.parentCategoryId != null) {
        const arr = childrenByParent.get(n.parentCategoryId) ?? [];
        arr.push(n);
        childrenByParent.set(n.parentCategoryId, arr);
      }
    }
    const result: TreeRow[] = [];
    for (const parent of topLevel) {
      result.push({ node: parent, isChild: false });
      for (const child of childrenByParent.get(parent.id) ?? []) {
        result.push({ node: child, isChild: true });
      }
    }
    return result;
  }, [categories]);

  // Top-level categories only (for the parent dropdown)
  const topLevelCategories = useMemo(
    () => (categories ?? []).filter((n) => n.parentCategoryId == null),
    [categories],
  );

  // ---- Write: create / update ----
  const upsert = useMutation({
    mutationFn: (form: CategoryForm) => {
      const body = {
        name: form.name,
        parentCategoryId: form.parentCategoryId ?? null,
      };
      if (editing) {
        // We don't have a rowVersion from the read endpoint, pass null (server will accept or 409)
        return categoriesAdminApi.update(editing.id, body, null);
      }
      return categoriesAdminApi.create(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: productKeys.categoryTree() });
      setCreating(false);
      setEditing(null);
    },
    onError: (err: Error) => {
      toast.error(`Save failed: ${err.message}`);
    },
  });

  // ---- Write: delete ----
  const remove = useMutation({
    mutationFn: (node: ProductCategoryNode) =>
      categoriesAdminApi.remove(node.id, null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: productKeys.categoryTree() });
    },
    onError: (err: Error) => {
      toast.error(`Cannot delete: ${err.message}`);
    },
  });

  async function handleDelete(node: ProductCategoryNode) {
    const ok = await confirm({
      title: "Delete category?",
      body: node.name,
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (ok) remove.mutate(node);
  }

  const drawerOpen = creating || editing !== null;
  const drawerTitle = editing ? "Edit category" : "New category";

  function toFormValues(node: ProductCategoryNode): CategoryForm {
    return {
      name: node.name,
      parentCategoryId: node.parentCategoryId,
    };
  }

  return (
    <ListPage backTo="/admin"
      title="Product Categories"
      primaryAction={
        <Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add Category</Button>
      }
    >
      {/* Tree table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ node, isChild }) => (
            <TableRow key={node.id}>
              <TableCell className={isChild ? "pl-6" : ""}>{node.name}</TableCell>
              <TableCell className="text-muted-foreground">
                {isChild ? "Subcategory" : "Category"}
              </TableCell>
              <TableCell>
                <div className="flex justify-end">
                  <RowActions
                    onEdit={() => setEditing(node)}
                    onDelete={() => void handleDelete(node)}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="py-6 text-center text-muted-foreground">
                No categories yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Create / Edit drawer */}
      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
        title={drawerTitle}
      >
        <EntityForm
          schema={categorySchema}
          defaultValues={editing ? toFormValues(editing) : BLANK_FORM}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              {/* Name */}
              <div className="space-y-1">
                <Label htmlFor="cat-name">Name</Label>
                <Input id="cat-name" {...form.register("name")} />
              </div>

              {/* Parent (top-level only; omit for children) */}
              <div className="space-y-1">
                <Label htmlFor="cat-parent">Parent category</Label>
                <select
                  id="cat-parent"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  {...form.register("parentCategoryId", {
                    setValueAs: (v: string) => (v === "" ? null : Number(v)),
                  })}
                >
                  <option value="">— None (top-level) —</option>
                  {topLevelCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
